/**
 * The recipient's acceptance flow — `m06-claim.js`, plus the three `m03-state.js` commands it is
 * allowed to call.
 *
 * This lives beside `public-page.ts` rather than in the shared `reference.ts` for the same reason
 * that file gives: `reference.ts` is frozen for the duration of the rebuild. Everything below is a
 * straight port — the two-language string table, the rejection vocabulary, the token lookup and
 * the one domain command that consumes a token and publishes a record.
 *
 * The ordering of the gate below is the part that matters most and the part easiest to lose. The
 * reference deliberately answers in the same order as `acceptClaim` so that the page a recipient
 * lands on and the answer their click gets can never disagree; the screen re-uses this function
 * instead of re-deciding, exactly as `Screens.claim` re-uses the domain command.
 */
import type { DemoCredential, DemoDatabase, DemoEvent } from '../data/types';
import { canTransition, prototypeNow } from './credential-register';
import { credentialViewState, trUpper } from './reference';
import type { Terms } from './render';

export type ClaimLanguage = 'tr' | 'en';

// ---------------------------------------------------------------------- the flow's two languages

export interface ClaimFieldLabels {
  title: string;
  holder: string;
  holderOrg: string;
  issuer: string;
  issue: string;
  expiry: string;
  noExpiry: string;
  id: string;
}

export interface ClaimStrings {
  /** Shown in the document's place when this language has no sealed document of its own. */
  noDocumentInLanguage: string;
  eyebrow: string;
  title: (T: Terms) => string;
  intro: string;
  consent: (isOrg: boolean) => string;
  accept: string;
  acceptLi: string;
  later: string;
  report: string;
  laterBox: (days: number) => string;
  validity: (d: string) => string;
  used: string;
  usedBtn: string;
  notFound: string;
  notFoundNote: string;
  expiredNote: string;
  reportTitle: string;
  reportLabel: string;
  reportPh: string;
  reportSend: string;
  reportOk: string;
  dl: ClaimFieldLabels;
  published: string;
  cancel: string;
  linkedinHint: string;
}

export const CLAIM_I18N: Record<ClaimLanguage, ClaimStrings> = {
  tr: {
    noDocumentInLanguage:
      'Bu sertifikanın Türkçe sürümü bulunmuyor. Kaydınız geçerlidir; belgeyi kendi dilinde görmek için dili değiştirin.',
    eyebrow: 'Onayınız bekleniyor',
    title: (T) => T.claimTitle,
    intro:
      'Aşağıdaki bilgiler doğruysa onaylayın; kaydınız o anda herkese açık doğrulama sayfasında yayınlanır. Onaylamadan hiçbir bilginiz yayınlanmaz.',
    consent: (isOrg) =>
      isOrg
        ? 'Onayla ve yayınla düğmesine basmanız, şirket adı ve sertifika bilgilerinin doğru olduğunu teyit ettiğiniz anlamına gelir.'
        : 'Onayla ve yayınla düğmesine basmanız, adınızın ve bilgilerin doğru olduğunu teyit ettiğiniz anlamına gelir.',
    accept: 'Onayla ve yayınla',
    acceptLi: "Onayla, yayınla ve LinkedIn'e geç",
    later: 'Şimdilik yayınlama',
    report: 'Hata bildir',
    laterBox: (days) =>
      `Kaydınız yayınlanmadı. Bu onay bağlantısı ${days} gün geçerlidir; e-postanızdaki bağlantıdan istediğiniz zaman geri dönebilirsiniz.`,
    validity: (d) => `Bu onay bağlantısı tek kullanımlıktır ve ${d} tarihine kadar geçerlidir.`,
    used: 'Bu onay bağlantısı daha önce kullanıldı; kaydınız yayında.',
    usedBtn: 'Doğrulama sayfamı aç',
    notFound: 'Onay bağlantısı bulunamadı',
    notFoundNote: 'Bağlantı eksik ya da hatalı kopyalanmış olabilir. E-postanızdaki bağlantıyı yeniden açın.',
    expiredNote: 'Yeni bir bağlantı için Happy Place to Work ile iletişime geçin.',
    reportTitle: 'Hata bildir',
    reportLabel: 'Neyi düzeltmeliyiz?',
    reportPh: 'ör. Soyadım «Yılmaz» değil «Yilmaz» yazılmış.',
    reportSend: 'Bildirimi gönder',
    reportOk:
      'Bildiriminiz Happy Place to Work ekibine iletildi (prototip simülasyonu). Düzeltme yapıldığında yeni bir onay e-postası alırsınız.',
    dl: {
      title: 'Sertifika adı',
      holder: 'Adınız',
      holderOrg: 'Şirket adı',
      issuer: 'Veren kurum',
      issue: 'Veriliş tarihi',
      expiry: 'Geçerlilik',
      noExpiry: 'Süresiz',
      id: 'Sertifika ID',
    },
    published: 'Kaydınız yayınlandı',
    cancel: 'Vazgeç',
    linkedinHint:
      "LinkedIn'e ekleme, onaydan sonra profil formunu açar ve alanları kopyalamanız için bir yardımcı gösterir; alanlar otomatik dolmaz.",
  },
  en: {
    noDocumentInLanguage:
      'An English version of this certificate is not available. Your record is valid; switch the language to see the document in the language it was issued in.',
    eyebrow: 'Awaiting your acceptance',
    title: (T) => T.claimTitle,
    intro:
      'If the details below are correct, accept them; your record is then published on the public verification page. Nothing is published until you accept.',
    consent: (isOrg) =>
      isOrg
        ? 'Pressing Accept and publish confirms that the company name and certificate details are correct.'
        : 'Pressing Accept and publish confirms that your name and the details are correct.',
    accept: 'Accept and publish',
    acceptLi: 'Accept, publish and continue to LinkedIn',
    later: "Don't publish yet",
    report: 'Report a mistake',
    laterBox: (days) =>
      `Your record was not published. This link stays valid for ${days} days; you can return from the link in your e-mail at any time.`,
    validity: (d) => `This link is single-use and valid until ${d}.`,
    used: 'This link was already used; your record is published.',
    usedBtn: 'Open my verification page',
    notFound: 'Acceptance link not found',
    notFoundNote: 'The link may be incomplete or copied incorrectly. Reopen the link from your e-mail.',
    expiredNote: 'Contact Happy Place to Work for a new link.',
    reportTitle: 'Report a mistake',
    reportLabel: 'What should we correct?',
    reportPh: 'e.g. my surname is spelled wrong.',
    reportSend: 'Send report',
    reportOk:
      'Your report was sent to the Happy Place to Work team (prototype simulation). You will receive a new acceptance e-mail after the correction.',
    dl: {
      title: 'Credential',
      holder: 'Your name',
      holderOrg: 'Company name',
      issuer: 'Issued by',
      issue: 'Issue date',
      expiry: 'Expiration',
      noExpiry: 'No expiration',
      id: 'Credential ID',
    },
    published: 'Your record is published',
    cancel: 'Cancel',
    linkedinHint:
      'Adding to LinkedIn opens the profile form after acceptance and shows a helper for copying the fields; the fields are not filled automatically.',
  },
};

// --------------------------------------------------------------------- the rejection vocabulary

export type ClaimRejectReason =
  | 'not_found'
  | 'wrong_state'
  | 'consumed'
  | 'revoked_token'
  | 'expired'
  | 'credential_revoked'
  | 'expired_credential';

export const CLAIM_REJECT_TR: Record<ClaimRejectReason, string> = {
  not_found: 'Bu onay bağlantısı tanınmadı.',
  wrong_state: 'Bu kayıt onay bekleyen durumda değil.',
  consumed: 'Bu onay bağlantısı daha önce kullanıldı.',
  revoked_token: 'Bu onay bağlantısı geçersiz kılınmış.',
  expired: 'Bu onay bağlantısının süresi doldu.',
  credential_revoked: 'Bu kayıt iptal edildiği için onaylanamaz.',
  expired_credential:
    'Bu kaydın geçerlilik süresi dolduğu için onaylanamaz. Yeni bir kayıt için Happy Place to Work ile iletişime geçin.',
};

export const CLAIM_REJECT_EN: Record<ClaimRejectReason, string> = {
  not_found: 'This acceptance link was not recognised.',
  wrong_state: 'This record is not awaiting acceptance.',
  consumed: 'This acceptance link has already been used.',
  revoked_token: 'This acceptance link has been invalidated.',
  expired: 'This acceptance link has expired.',
  credential_revoked: 'This record was revoked and can no longer be accepted.',
  /* Upstream's English table has no `expired_credential`, and the lookup below falls back to
     `not_found` for a missing key. Reproduced rather than filled in: inventing the sentence would
     put a string on the screen that the frozen reference never shows. */
  expired_credential: 'This acceptance link was not recognised.',
};

export const claimRejectText = (reason: ClaimRejectReason, lang: ClaimLanguage): string =>
  (lang === 'en' ? CLAIM_REJECT_EN : CLAIM_REJECT_TR)[reason] || CLAIM_REJECT_TR.not_found;

// ------------------------------------------------------------------------------- token lookup

/** `stripToken` — `m01-utils.js:148`. Case, separators and the `CLM` prefix are all noise. */
export const stripToken = (t: string | null | undefined): string =>
  trUpper(String(t ?? ''))
    .replace(/[^A-Z0-9]/g, '')
    .replace(/^CLM/, '');

interface OutboxEntry {
  rawToken?: string;
}

function outbox(db: DemoDatabase): Record<string, OutboxEntry> {
  const box = (db as { outbox?: unknown }).outbox;
  return box !== null && typeof box === 'object' ? (box as Record<string, OutboxEntry>) : {};
}

/**
 * `findByRawClaimToken` — `m03-state.js:178`.
 *
 * The reference hashes the presented token and matches `claimToken.hash`; nothing anywhere stores
 * the raw one except the transient outbox the issuing session keeps in memory. This demo matches
 * against that same outbox, which resolves every link the demo can actually produce and keeps the
 * comparison synchronous — a Web Crypto digest is a promise, and a claim page that renders one
 * frame later cannot be captured deterministically.
 *
 * API GAP: public.claim_lookup — the server hashes the presented token with the token's own
 * algorithm and looks the record up by hash alone, in constant time, with no raw token at rest.
 */
export function findByRawClaimToken(
  db: DemoDatabase,
  rawToken: string | null | undefined,
): DemoCredential | null {
  const norm = stripToken(rawToken);
  if (!norm) return null;
  const box = outbox(db);
  for (const c of db.credentials) {
    if (!c.claimToken) continue;
    const raw = box[c.id]?.rawToken;
    if (raw && stripToken(raw) === norm) return c;
  }
  return null;
}

// --------------------------------------------------------------------------------- the gate

interface TokenFacts {
  expiresAt: string | null;
  consumedAt: string | null;
  revokedAt: string | null;
}

function tokenFacts(c: DemoCredential): TokenFacts {
  const t = c.claimToken ?? {};
  const str = (k: string): string | null => (typeof t[k] === 'string' ? (t[k] as string) : null);
  return { expiresAt: str('expiresAt'), consumedAt: str('consumedAt'), revokedAt: str('revokedAt') };
}

/** Which of `Screens.claim`'s seven answers this link gets. */
export type ClaimGate =
  | { kind: 'not_found' }
  | { kind: 'credential_revoked'; credential: DemoCredential }
  | { kind: 'used'; credential: DemoCredential }
  | { kind: 'token_revoked'; credential: DemoCredential }
  | { kind: 'token_expired'; credential: DemoCredential }
  | { kind: 'wrong_state'; credential: DemoCredential }
  | { kind: 'claimable'; credential: DemoCredential };

/**
 * `Screens.claim`'s own sequence of tests, in its own order — `m06-claim.js:64`.
 *
 * A consumed token answers «already published» even when the record has since been revoked in a
 * different order than a reader might expect; the order is upstream's and is not tidied here,
 * because the screen and the command have to reject for the same reason.
 */
export function claimGate(c: DemoCredential | null, now: string = prototypeNow()): ClaimGate {
  if (!c) return { kind: 'not_found' };
  const st = credentialViewState(c);
  const tok = tokenFacts(c);
  if (st === 'revoked') return { kind: 'credential_revoked', credential: c };
  if (tok.consumedAt || st === 'active' || st === 'expired') return { kind: 'used', credential: c };
  if (tok.revokedAt) return { kind: 'token_revoked', credential: c };
  if (tok.expiresAt && !(now < tok.expiresAt)) return { kind: 'token_expired', credential: c };
  if (st !== 'pending_acceptance') return { kind: 'wrong_state', credential: c };
  return { kind: 'claimable', credential: c };
}

// ------------------------------------------------------------------------------ the command

export interface AcceptOutcome {
  ok: boolean;
  reason?: ClaimRejectReason;
  message?: string;
  /** A second click on an already-consumed link is not an error; it lands on the public page. */
  idempotent?: boolean;
  credential?: DemoCredential;
}

/**
 * `acceptClaim` — `m03-state.js:190`, evaluated without writing.
 *
 * Hash, state, consumption, revocation and expiry are one decision, not five the caller may pick
 * from, which is why the screen asks this instead of reading `claimToken` itself.
 */
export function evaluateAccept(
  db: DemoDatabase,
  rawToken: string,
  now = prototypeNow(),
  /**
   * The record the caller already resolved, when it has one.
   *
   * The demo does not pass it and the lookup below is the reference's own. The product does: the
   * SERVER hashed the token and answered which record it belongs to, and this surface keeps no
   * outbox and no raw token to look one up with. Without it the courtesy check would answer
   * «tanınmadı» for a link the server had just accepted, and the command would never be sent —
   * which is the one way a pre-flight check can be worse than no check at all.
   */
  resolved?: DemoCredential | null,
): AcceptOutcome {
  const c = resolved === undefined ? findByRawClaimToken(db, rawToken) : resolved;
  if (!c) return { ok: false, reason: 'not_found', message: CLAIM_REJECT_TR.not_found };
  const t = tokenFacts(c);
  if (c.state === 'revoked')
    return {
      ok: false,
      reason: 'credential_revoked',
      message: CLAIM_REJECT_TR.credential_revoked,
      credential: c,
    };
  if (t.consumedAt)
    return {
      ok: false,
      reason: 'consumed',
      message: CLAIM_REJECT_TR.consumed,
      credential: c,
      idempotent: c.state === 'active',
    };
  if (t.revokedAt)
    return { ok: false, reason: 'revoked_token', message: CLAIM_REJECT_TR.revoked_token, credential: c };
  if (c.state !== 'pending_acceptance')
    return { ok: false, reason: 'wrong_state', message: CLAIM_REJECT_TR.wrong_state, credential: c };
  if (!(t.expiresAt && now < t.expiresAt))
    return { ok: false, reason: 'expired', message: CLAIM_REJECT_TR.expired, credential: c };
  /* §7 — an already-lapsed credential is refused by default. A release may opt in with
     `allowHistoricalClaim`, and then the published page reads «expired» from the first second. */
  const today = now.slice(0, 10);
  if (c.expiresAt && c.expiresAt < today && c.release.allowHistoricalClaim !== true)
    return {
      ok: false,
      reason: 'expired_credential',
      message: CLAIM_REJECT_TR.expired_credential,
      credential: c,
    };
  if (!canTransition(c.state, 'active'))
    return { ok: false, reason: 'wrong_state', message: CLAIM_REJECT_TR.wrong_state, credential: c };
  return { ok: true, credential: c };
}

let seq = 0;

function appendEvent(
  db: DemoDatabase,
  c: DemoCredential,
  o: {
    type: string;
    command: string;
    at: string;
    actor?: string;
    actorType?: string;
    meta?: Record<string, unknown>;
    beforeDigest?: string;
    afterDigest?: string;
  },
): void {
  seq += 1;
  const ev: DemoEvent = {
    id: `ev_claim_${seq.toString(36)}`,
    credentialId: c.id,
    type: o.type,
    command: o.command,
    at: o.at,
    actor: o.actor ?? 'Sistem',
    actorType: o.actorType ?? 'system',
    reason: null,
    beforeDigest: o.beforeDigest ?? null,
    afterDigest: o.afterDigest ?? null,
  };
  if (o.meta) ev.meta = o.meta;
  c.events.push(ev);
  c.updatedAt = ev.at;
  db.eventLog.push(ev);
}

/**
 * Consumes the token and publishes the record, in the draft database the provider hands out.
 *
 * The checks are run again here rather than trusted from the caller's earlier `evaluateAccept`,
 * so two clicks that race each other produce one acceptance and one no-op.
 *
 * API GAP: public.claim_accept — token consumption, the `pending_acceptance → active` transition,
 * the publication timestamp and both audit rows in ONE transaction; a second call with the same
 * token answers idempotently with the published record instead of publishing twice.
 */
export function acceptClaimInDraft(db: DemoDatabase, rawToken: string): AcceptOutcome {
  const now = prototypeNow();
  const outcome = evaluateAccept(db, rawToken, now);
  if (!outcome.ok || !outcome.credential) return outcome;
  const c = outcome.credential;
  const before = c.state;
  c.claimToken = { ...(c.claimToken ?? {}), consumedAt: now };
  c.state = 'active';
  c.publishedAt = now;
  appendEvent(db, c, {
    type: 'accepted',
    command: 'acceptClaim',
    actor: 'Alıcı',
    actorType: 'recipient',
    at: now,
    meta: { via: 'claim_link' },
    beforeDigest: before,
    afterDigest: c.state,
  });
  appendEvent(db, c, { type: 'published', command: 'acceptClaim', actor: 'Sistem', at: now });
  return { ok: true, credential: c };
}

/**
 * `reportClaimIssue` — the recipient's «this is wrong» path (`m06-claim.js:131`).
 *
 * It writes an audit event and parks the message on the record, which is what puts the row on the
 * panel's «Dikkat isteyenler» list. It deliberately does NOT change the record's state: a
 * correction is an issuer act, and nothing a recipient types may move the lifecycle.
 *
 * API GAP: public.claim_report_issue — an `error_reported` audit row and a stored report, rate
 * limited per token, with the message treated as untrusted text everywhere it is later shown.
 */
export function reportClaimIssueInDraft(db: DemoDatabase, credentialId: string, message: string): void {
  const c = db.credentials.find((x) => x.id === credentialId);
  if (!c) return;
  const at = prototypeNow();
  appendEvent(db, c, {
    type: 'error_reported',
    command: 'reportClaimIssue',
    actor: 'Alıcı',
    actorType: 'recipient',
    at,
    meta: { message },
  });
  c.issueReport = { message, at };
}

/** `DB.settings.claim.tokenValidityDays`, narrowed — the seed types the settings block loosely. */
export function claimValidityDays(db: DemoDatabase): number {
  const v = (db.settings.claim as { tokenValidityDays?: unknown }).tokenValidityDays;
  return typeof v === 'number' ? v : 30;
}
